import { useState, useEffect } from 'react'
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    Timestamp
} from 'firebase/firestore'
import { getFirebaseDb } from '../config/firebase'
import type { Product, ProductStatus } from '../types'

interface UseProductsOptions {
    category?: string
    status?: ProductStatus
    search?: string
}

export function useProducts(options: UseProductsOptions = {}) {
    const [products, setProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const db = getFirebaseDb()

    useEffect(() => {
        // Removed orderBy to avoid index requirements for now
        let q = query(collection(db, 'products'))

        if (options.category) {
            q = query(q, where('category.main', '==', options.category))
        }

        if (options.status) {
            q = query(q, where('status', '==', options.status))
        }

        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                let productData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Product[]

                // Client-side sorting by createdAt desc
                productData.sort((a, b) => {
                    const dateA = a.createdAt?.seconds || 0
                    const dateB = b.createdAt?.seconds || 0
                    return dateB - dateA
                })

                // Client-side search filtering
                if (options.search) {
                    const searchLower = options.search.toLowerCase()
                    productData = productData.filter(p =>
                        p.name.toLowerCase().includes(searchLower) ||
                        p.description.toLowerCase().includes(searchLower)
                    )
                }

                setProducts(productData)
                setLoading(false)
            },
            (err) => {
                console.error('Error fetching products:', err)
                setError(err.message)
                setLoading(false)
            }
        )

        return () => unsubscribe()
    }, [db, options.category, options.status, options.search])

    return { products, loading, error }
}

export function useProduct(productId: string) {
    const [product, setProduct] = useState<Product | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const db = getFirebaseDb()

    useEffect(() => {
        if (!productId) {
            setLoading(false)
            return
        }

        const fetchProduct = async () => {
            try {
                const docRef = doc(db, 'products', productId)
                const docSnap = await getDoc(docRef)

                if (docSnap.exists()) {
                    setProduct({ id: docSnap.id, ...docSnap.data() } as Product)
                } else {
                    setError('Product not found')
                }
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to fetch product'
                setError(message)
            } finally {
                setLoading(false)
            }
        }

        fetchProduct()
    }, [db, productId])

    return { product, loading, error }
}

// Admin product management hooks
export function useProductManagement() {
    const db = getFirebaseDb()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const createProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
        setLoading(true)
        setError(null)
        try {
            const docRef = await addDoc(collection(db, 'products'), {
                ...productData,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            })
            return docRef.id
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to create product'
            setError(message)
            throw err
        } finally {
            setLoading(false)
        }
    }

    const updateProduct = async (productId: string, updates: Partial<Product>) => {
        setLoading(true)
        setError(null)
        try {
            await updateDoc(doc(db, 'products', productId), {
                ...updates,
                updatedAt: Timestamp.now()
            })
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update product'
            setError(message)
            throw err
        } finally {
            setLoading(false)
        }
    }

    const deleteProduct = async (productId: string) => {
        setLoading(true)
        setError(null)
        try {
            await deleteDoc(doc(db, 'products', productId))
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete product'
            setError(message)
            throw err
        } finally {
            setLoading(false)
        }
    }

    return {
        loading,
        error,
        createProduct,
        updateProduct,
        deleteProduct
    }
}
